import { useCallback, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { settingsService } from "services/settings/settings.service"
import { SettingsResetTarget, SettingsResetTargetsResponse } from "services/settings/type"

/**
 * Module-level cache so every section's reset button shares one request.
 * The registry only changes on deploy, so a per-session fetch is enough.
 */
let inFlight: Promise<SettingsResetTargetsResponse | null> | null = null

const load = (): Promise<SettingsResetTargetsResponse | null> => {
  if (!inFlight) inFlight = settingsService.getResetTargets()
  return inFlight
}

/** Drops the cache so the next consumer refetches. Exposed for tests and for a hard refresh. */
export const invalidateResetTargets = (): void => {
  inFlight = null
}

export interface UseResetTargetsResult {
  targets: SettingsResetTarget[]
  /** The phrase `POST /settings/reset-all` requires; empty until the registry has loaded. */
  resetAllConfirmation: string
  loading: boolean
  find: (id: string) => SettingsResetTarget | undefined
  /**
   * Operator-facing copy for a target. The API ships structure only, so the wording comes
   * from `settings.danger.targets.<id>` here and stays translatable.
   *
   * @param sectionLabel names the section in the generic fallback used when the API grows a
   * target this build has no copy for.
   */
  describe: (id: string, sectionLabel: string) => string
  refresh: () => Promise<void>
}

export function useResetTargets(): UseResetTargetsResult {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [data, setData] = useState<SettingsResetTargetsResponse | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchTargets = useCallback(async () => {
    setLoading(true)
    try {
      setData(await load())
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    let active = true

    load()
      .then((response) => {
        if (active) setData(response)
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [])

  const refresh = useCallback(async () => {
    invalidateResetTargets()
    await fetchTargets()
  }, [fetchTargets])

  const targets = data?.targets ?? []

  return {
    targets,
    resetAllConfirmation: data?.resetAllConfirmation ?? "",
    loading,
    find: useCallback((id: string) => targets.find((target) => target.id === id), [targets]),
    describe: useCallback(
      (id: string, sectionLabel: string) =>
        t(`settings.danger.targets.${id}`, { defaultValue: t("settings.reset.confirmFallback", { section: sectionLabel }) }),
      [t]
    ),
    refresh
  }
}
